import * as a from '@mitranim/js/all.mjs'
import * as ob from '@mitranim/js/obs.mjs'
import * as e from 'emerge'
import * as c from './conf.mjs'
import * as ut from './util-text.mjs'
import * as uti from './util-time.mjs'
import * as ue from './util-err.mjs'
import * as ul from './util-log.mjs'
import * as ud from './util-dom.mjs'

export function isMeaningless(val) {
  return (
    a.isNil(val) ||
    a.isNaN(val) ||
    (a.isStr(val) && a.isBlank(val)) ||
    (a.isList(val) && a.isEmpty(val)) ||
    (a.isStruct(val) && a.isEmpty(val))
  )
}

export function isMeaningful(val) {return !isMeaningless(val)}

export function getId(val)              {return a.optStruct(val)?.id}
export function getIds(vals)            {return a.mapCompact(vals, getId)}
export function getOriginId(val)        {return a.optStruct(val)?.originId}
export function getItems(val)           {return a.optStruct(val)?.items}
export function getName(val)            {return a.optStruct(val)?.name}
export function getTitle(val)           {return a.optStruct(val)?.title || a.optStruct(val)?.name}
export function getTitles(val)          {return a.mapCompact(val, getTitle)}
export function getPath(val)            {return a.optStruct(val)?.path}
export function getValue(val)           {return a.optStruct(val)?.value}
export function getSlug(val)            {return a.optStruct(val)?.slug}
export function getSlugs(val)           {return a.mapCompact(val, getSlug)}
export function getBody(val)            {return a.optStruct(val)?.body}
export function getDiscountPercent(val) {return a.optStruct(val)?.discountPercent}
export function getPriceCents(val)      {return a.optStruct(val)?.priceCents}
export function getPriceCurrency(val)   {return a.optStruct(val)?.priceCurrency}
export function getExternalId(val)      {return a.optStruct(val)?.externalId}
export function getIsDone(val)          {return a.optStruct(val)?.isDone}
export function getAbbr(val)            {return a.optStruct(val)?.abbr}
export function getPage(val)            {return a.optStruct(val)?.page}

/*
Normalizes nils by converting `undefined` to `null`. Preserves other values
as-is. Useful in multiple scenarios.

In JSON encoding, dict fields whose value is `undefined` are completely
omitted, while `null` fields are preserved. When submitting a form to a "PATCH"
endpoint, we usually want to ensure that any fields that were nullified during
editing are actually sent rather than ignored.

In DOM inputs, setting the value to `null` tends to clear the value, while
`undefined` tends to get stringified as `'undefined'`.
*/
export function normNil(val) {return val ?? null}

export function sortByNameCaseInsensitive(src) {
  a.reqArr(src).sort(function compare(one, two) {
    return a.compare(getNameLowerCase(one), getNameLowerCase(two))
  })
}

function getNameLowerCase(val) {return a.lower(getName(val))}

export function isGeoPoint(val) {return a.isStruct(val) && a.isFin(val.lat) && a.isFin(val.lng)}
export function reqGeoPoint(val) {return a.req(val, isGeoPoint)}
export function optGeoPoint(val) {return a.opt(val, isGeoPoint)}
export function onlyGeoPoint(val) {return a.only(val, isGeoPoint)}

export function validOrDefaultGeoPoint(val) {
  return validOrMissingGeoPoint(val) || {
    lng: c.DEFAULT_GEO_LONGITUDE,
    lat: c.DEFAULT_GEO_LATITUDE,
  }
}

// Seems redundant with `onlyGeoPoint`, TODO remove.
export function validOrMissingGeoPoint(val) {
  const {lng, lat} = a.laxStruct(val)
  return onlyGeoPoint({lng, lat})
}

/*
  Reference on Google Places, see class `Place`:
  https://developers.google.com/maps/documentation/javascript/places-autocomplete.
*/
export function googlePlaceToGeoPoint(place) {
  return googleLocGeoPoint(place?.geometry?.location)
}

/*
  Reference on Google Places, see class `Place`:
  https://developers.google.com/maps/documentation/javascript/places-autocomplete.
*/
export function getPlaceId(val) {return a.optStr(a.optStruct(val)?.place_id)}

// https://developers.google.com/maps/documentation/javascript/reference/map
export function googleMapGeoPoint(map) {
  return googleLocGeoPoint(map?.getCenter())
}

function googleLocGeoPoint(loc) {
  if (!loc) return undefined
  return validOrMissingGeoPoint({lng: loc.lng(), lat: loc.lat()})
}

// https://developer.mozilla.org/en-US/docs/Web/API/GeolocationPosition
export function geoPosToGeoPoint(pos) {
  const {coords} = a.laxStruct(pos)
  const {longitude: lng, latitude: lat} = a.laxStruct(coords)
  return a.req({lng, lat}, isGeoPoint)
}

// https://developer.mozilla.org/en-US/docs/Web/API/GeolocationPosition
export function geoPosToSecs(pos) {
  const {timestamp} = a.laxStruct(pos)
  return uti.millisToSecs(uti.toValidTimestamp(timestamp))
}

/*
Must be used to wrap async imports:

    u.imp(import('./some-module'))

See `onImportErr` for an explanation.
*/
export function imp(val) {
  return Promise.resolve(val).catch(ue.onImportErr)
}

// Short for "truthy equal". NOT an inverse of `tneq`.
export function teq(one, two) {return a.truthy(one && two) && a.is(one, two)}

// Short for "truthy not equal". NOT an inverse of `teq`.
export function tneq(one, two) {return a.truthy(one || two) && !a.is(one, two)}

export class DeinitQue extends a.Que {deinit() {this.run()}}

/*
Abstract base class for "alternative" promise implementations / interfaces /
wrappers. See subclasses.
*/
class PromiseAlt extends a.Emp {
  async promise() {throw ue.errImplementInSubclass()}
  then(...val) {return this.promise().then(...val)}
  catch(...val) {return this.promise().catch(...val)}
  finally(...val) {return this.promise().finally(...val)}
}

export function plist() {return new PromiseList()}

/*
Alternative to `Promise.all`. Avoids a massive gotcha inherent to the API of
`Promise.all`. Compare the following:

    try {
      await Promise.all([
        Promise.reject(`reason`),
        syncThrow(),
      ])
    } catch {}

    try {
      await new PromiseList()
        .add(Promise.reject(`reason`))
        .add(syncThrow())
    } catch {}

    function syncThrow() {throw Error(`some_exception`)}

When executed in any non-browser environment, the version using `Promise.all`
crashes the process in a future tick of the event loop. The version using
`PromiseList` does not. The difference is that `PromiseList`'s builder API
forces the caller to immediately register every new promise before generating
the next promise, and immediately suppresses unhandled rejections. If the
entire list is unhandled due to a synchronous exception, there is no unhandled
rejection, and the process doesn't crash. This is inconsistent with the design
of `Promise.all`, but satisfies our requirements, indicating that `Promise.all`
is fundamentally unsuitable for use in non-browser environments where unhandled
rejections crash the process.

Some people may argue that unhandled rejections always indicate buggy /
unfinished code. I disagree. The example above (with `Promise.all`) is correct
in the sense that the author of the code didn't forget to handle promises. The
code attempts to generate 2 promises and pass them to `Promise.all`, exactly as
intended by the design of `Promise.all` and every example you'll find in every
tutorial. The design itself has a "bug" that needs fixing.

See `test-net.mjs`. TODO consider moving to `@mitranim/js`.
*/
export class PromiseList extends PromiseAlt {
  vals = []

  add(val) {
    suppressPromise(val)
    this.vals.push(val)
    return this
  }

  addOpt(val) {
    if (a.isPromise(val)) this.add(val)
    return this
  }

  map(src, fun) {
    a.reqFun(fun)
    for (const [key, val] of a.entries(src)) this.add(fun(val, key))
    return this
  }

  promise() {return Promise.all(this.vals)}
  vac() {return a.vac(this.vals.length) && this}

  static map(src, fun) {return new this().map(src, fun)}
}

export function pdict() {return new PromiseDict()}

// See `PromiseList` for explanation.
export class PromiseDict extends PromiseAlt {
  keys = []
  vals = []

  // Preferred API for external callers.
  funs(src) {
    for (const key of a.structKeys(src)) this.add(key, src[key]())
    return this
  }

  mapDict(src, fun) {
    a.reqFun(fun)
    for (const key of a.structKeys(src)) this.add(key, fun(src[key], key))
    return this
  }

  // For internal use. Prefer `.funs` externally.
  add(key, val) {
    a.reqKey(key)
    suppressPromise(val)
    this.keys.push(key)
    this.vals.push(val)
    return this
  }

  /*
  Assumes that the set of keys remains unchanged during the await. If we
  expected changes, we would have to store the initial length and reslice after
  the await in case of mismatch, adding more code for an edge case that
  shouldn't even happen.
  */
  async promise() {return zip2(this.keys, await Promise.all(this.vals))}

  static mapDict(src, fun) {return new this().mapDict(src, fun)}
}

function suppressPromise(val) {val?.then?.(a.nop, a.nop)}

/*
Short for "future". Like `Promise`, but allows to control the promise's state
externally, by calling `.resolve`, `.reject`, or `.done`.

TODO consider moving to `@mitranim/js`.
*/
export class Fut extends a.Emp {
  constructor() {
    super()
    const init = (done, fail) => {
      this.resolve = done
      this.reject = fail
    }
    this.task = new Promise(init)
  }

  // Implement the promise interface. Compatible with `async/await`.
  then(...val) {return this.task.then(...val)}

  done(err, val) {
    if (err) this.reject(err)
    else this.resolve(val)
  }

  deinit() {this.reject(new a.AbortError())}
}

export function once(fun) {
  a.reqFun(fun)
  function once() {
    if (!once.done) {
      once.val = fun()
      once.done = true
    }
    return once.val
  }
  return once
}

/*
The "match" must be received from `RegExp.prototype.exec` or as arguments
to a callback in `String.prototype.replace`.

Caution: capture group index starts at 1!
*/
export function matchIndex(match) {
  a.reqList(match)
  let ind = 0
  while (++ind < match.length) {
    if (a.isSome(match[ind])) return ind - 1
  }
  return -1
}

/*
This function is an exersise for the reader. If you can figure out what's going
on here, write to `#corespirit` on Slack to brag about it.
*/
export function stabilize(val, fun, ...args) {
  while (!a.is(val, val = fun(val, ...args))); return val
}

// Note: this must be an enumerable non-symbol property for compatibility with
// `emerge`, which is used for timespan patching. After removing `emerge`, we
// may convert this to a symbolic or non-enumerable property.
export const KEY = `_key`

export function getSecretKey(val) {return val?.[a.reqKey(KEY)]}

export function addSecretKey(val, key) {
  if (a.isNil(val)) return val
  return {...a.laxDict(val), [a.reqKey(KEY)]: key}
}

export function addSecretRandomKey(val) {
  return addSecretKey(val, ut.rndId())
}

export function addSecretRandomKeys(vals) {
  return a.map(vals, addSecretRandomKey)
}

/*
Takes a dict and returns true if exactly one of its fields is non-nil.
"Mutex" is short for "mutually exclusive".

Known deficiency: always iterates the whole dict, even though we could stop
earlier when the result is provably `false`. Fixing this issue requires too
much code. Also, most of the time we expect `true`, which can be proven only by
iterating the whole input. This function should be used only for small dicts.
*/
export function isMutex(val) {return a.count(val, a.isSome) === 1}

export function mobileLinkByUserAgent(val) {
  if (isUserAgentApple(val)) return c.MOBILE_LINK_APPLE
  if (isUserAgentAndroid(val)) return c.MOBILE_LINK_ANDROID
  return undefined
}

// TODO: consider \b.
export function isUserAgentApple(val) {return /iPhone|iPad|iPod/i.test(a.laxStr(val))}

// TODO: consider \b.
export function isUserAgentAndroid(val) {return /Android/i.test(a.laxStr(val))}

// Open mobile app with current session sharing or without.
export function getMobileLinkNoSession(ctx) {
  return mobileLinkByUserAgent(ctx.env.userAgent())
}

async function getMobileLinkSessioned(ctx) {
  try {
    const sessionId = ctx.env.getSessionId()
    const {shortLink} = await (await imp(import(`./net.mjs`))).fetchFds(ctx, {sessionId})
    return shortLink
  }
  catch (err) {
    // TODO What should I do with err because we already lost the page.
    ul.maybeLogErr(err)
  }

  return undefined
}

async function getMobileLink(ctx) {
  return (await getMobileLinkSessioned(ctx)) || getMobileLinkNoSession(ctx)
}

export async function redirectMobile(ctx) {
  ud.hardRedirect(await getMobileLink(ctx))
}

// Tiny utility for comparator functions for `Array..sort`.
export function compare(one, two) {
  return (
    (one && !two) ? -1 :
    (!one && two) ? +1 :
    0
  )
}

// Note: this is just a tiny async loader.
import {loadStripe} from 'stripe-js'

export const initStripe = once(function initStripe() {
  /*
  Prevents an insane gotcha in the DOM API. Explanation:

    * We have elements with id `#Stripe`.
    * When `[id=Stripe]` exists in the DOM, accessing `window.Stripe` magically
      returns a DOM node. This is standard behavior for any element whose id is
      a valid JS identifier.
    * Stripe SDK fails to set its own global `Stripe`.
    * Code attempting to use `window.Stripe` finds the DOM node instead of the
      Stripe SDK namespace.

  Assigning an own property to `window` suppresses the magic getter behavior.
  */
  if (!a.hasOwn(window, `Stripe`)) window.Stripe = undefined
  return loadStripe(c.STRIPE_PUBLIC_KEY).catch(ue.onImportErr)
})

/*
Related reading:

  https://stripe.com/docs/stripe-js/elements/payment-request-button?html-or-react=html#html-js-complete-payment

Be careful with this function. We must ALWAYS call `.complete` on this event,
otherwise a native browser payment dialogue might get stuck forever.
*/
export async function withStripeEvent(event, fun) {
  try {
    const val = await fun(event)
    event.complete(`success`)
    return val
  }
  catch (err) {
    event.complete(`fail`)

    /*
    Stripe SDK "helpfully" hides event processing errors.
    We have to log them ourselves.
    */
    console.error(`unexpected error while processing Stripe event:`, err)

    throw err
  }
}

// Antipattern caused by React. TODO remove.
export function bindAll(tar) {
  bindFrom(tar, Object.getPrototypeOf(tar))
}

function bindFrom(tar, proto) {
  if (!proto || proto === Object.prototype) return
  const src = Object.getOwnPropertyDescriptors(proto)
  for (const key of a.structKeys(src)) bindAt(src[key], key, tar)
  bindFrom(tar, Object.getPrototypeOf(proto))
}

function bindAt(desc, key, tar) {
  if (key === `constructor` || a.hasOwn(tar, key)) return
  const {value} = desc
  if (a.isFun(value)) a.priv(tar, key, value.bind(tar))
}

export function privs(tar, src) {
  for (const key of a.structKeys(src)) a.priv(tar, key, src[key])
}

/*
Deeply walks an arbitrary struct, calling the given function on the root and
each struct field. For non-structs, this is simply pass-through. Supports
async. Combines all calls via `Promise.all`.

Unused, TODO drop. Deep-walking can be convenient, but can also lead to inferior
APIs and has measurable overhead.
*/
export function deep(src, fun) {
  a.reqFun(fun)
  const buf = new PromiseList()

  function deepRec(src) {
    if (!isDeepWalkable(src)) return

    buf.addOpt(fun(src))

    // Equivalent to `a.each` but slightly faster here.
    for (const key of Object.keys(src)) deepRec(src[key])

    // Equivalent to `a.each` but slightly faster here.
    if (a.isParentNode(src)) for (const val of src.childNodes) deepRec(val)
  }

  deepRec(src)
  return buf
}

/*
Motive: avoid deep-walking iterable collections, but do deep-walk DOM nodes.
Some DOM nodes, such as `form`, have a "list" interface.
*/
function isDeepWalkable(val) {
  return a.isObj(val) && (a.isNode(val) || a.isStruct(val))
}

// Same as `deep` but synchronous.
export function deepSync(val, fun) {
  a.reqFun(fun)

  void function deepSyncRec(val) {
    if (a.isStruct(val)) fun(val)
    a.each(val, deepSyncRec)
  }(val)
}

export function autoPreinitOpt(val) {
  return a.hasMeth(val, `autoPreinit`) ? val.autoPreinit() : undefined
}

export function autoInitOpt(val) {
  return a.hasMeth(val, `autoInit`) ? val.autoInit() : undefined
}

export function preinitOpt(val) {
  return a.hasMeth(val, `preinit`) ? val.preinit() : undefined
}

export function initOpt(val) {
  return a.hasMeth(val, `init`) ? val.init() : undefined
}

export function isClearer(val) {return a.hasMeth(val, `clear`)}

/*
Short for "buket dynamic variable". Represents a dynamically-scoped variable
rather than a regular lexically-scoped variable. Value must be `BukOut`.

TODO move to `dat-model.mjs` and use `a.TypedDyn`:

  export const bukDyn = new class BukDyn extends a.TypedDyn {
    reqVal(val) {return a.optInst(val, BukOut)}
  }()
*/
export const bukDyn = new a.Dyn()

export const bukKey = Symbol.for(`buk`)

/*
For usage example, see `BukOut.prototype.mut`.

Implementation note. Would prefer to use `.#buk` instead of `[bukKey]` because
it's simpler, but our bundler seems to have a bug that breaks private fields in
mixins.
*/
export function MixBuketed(cls) {
  return class MixBuketedCls extends cls {
    constructor(...val) {
      super(...val)
      this[bukKey] = bukDyn.get()
    }
    buk() {return this[bukKey]}
  }
}

// TODO consider converting to mixin.
export class Enved extends a.Emp {
  constructor(env) {
    super()
    a.priv(this, `env`, a.optStruct(env))
  }
}

export function MixCtxed(cls) {
  return class MixCtxedCls extends cls {
    setCtx(val) {return a.priv(this, `ctx`, a.optStruct(val)), this}
    pageDat() {return this.ctx?.pageDat}
    buk() {return this.pageDat()?.buk}
  }
}

export class Ctxed extends MixCtxed(a.Emp) {
  constructor(ctx) {super().setCtx(ctx)}
}

// TODO consider converting to mixin.
export class DeCtxed extends Ctxed {
  constructor(ctx) {
    super(ctx)
    return ob.de(this)
  }
}

export function MixStructCtxed(cls) {
  return class MixStructCtxedCls extends cls {
    static spec = {
      ctx: a.reqStruct,
    }

    // This hack makes `.ctx` private. A non-enumerable field would be simpler,
    // but `a.Struct` currently doesn't support non-enumerable fields.
    set ctx(val) {a.priv(this, `ctx`, val)}
  }
}

export function final(tar, key, val) {
  Object.defineProperty(tar, a.reqObjKey(key), {
    value: val,
    writable: false,
    enumerable: true,
    configurable: true,
  })
  return val
}

// Variant of `a.pk` with support for `.id`.
export function pk(val) {return a.reqPk(pkOpt(val))}

// Variant of `a.pkOpt` with support for `.id`.
export function pkOpt(val) {return a.pkOpt(val) ?? val?.id}

export function pksOpt(val) {return a.mapCompact(val, pkOpt)}

export function isPerfectMatchRank(rank) {return a.reqNum(rank) && rank > c.NOT_PERFECT_BEFORE}
export function isPerfectMatchRankOpt(rank) {return !a.isNum(rank) ? undefined : isPerfectMatchRank(rank)}

/*
Should be used for errors that don't have anywhere to be rendered.
TODO: where should this function be located?

Note: this function should support the case when error is nil. In this case, if
we support showing multiple errors, nil error should be ignored, and if we
support showing only one error, any previous error should be removed.
*/
export async function onUnhandledErr(err) {
  if (ue.isErrUnauthed(err)) {
    const mod = await imp(import(`./views/auth-magic.mjs`))
    mod.openAuthMagic()
    return
  }

  const mod = await imp(import(`./elem-misc.mjs`))
  mod.Notif.err(err)
}

export function trySkip(fun) {trySkipAsync(fun).catch(console.error || a.nop)}
async function trySkipAsync(fun) {await fun()}

export function isValidInd(len, ind) {
  return a.isNat(len) && a.isNat(ind) && ind >= 0 && ind < len
}

function listGet(list, ind) {
  list = a.optList(list)
  ind = a.optInt(ind)
  return list && isValidInd(list.length, ind) ? list[ind] : undefined
}

export function listPrev(list, elem) {
  return listGet(list, a.indexOf(list, elem) - 1)
}

export function listNext(list, elem) {
  return listGet(list, a.indexOf(list, elem) + 1)
}

export class FunQue extends a.Que {
  deinit() {this.open().close()}
}

/*
Takes list, chunk size, and chunk index. Returns chunk number N from the set of
all possible chunks of this size. Because this is often called in a loop, the
caller must convert the source collection to `Array` before looping to avoid
repeated conversion.

Examples:

  src = [10, 20, 30, 40, 50, 60, 70, 80, 90]

  chunkAt(src, 1, 0) -> [10]
  chunkAt(src, 1, 1) -> [20]

  chunkAt(src, 2, 0) -> [10, 20]
  chunkAt(src, 2, 1) -> [30, 40]

  chunkAt(src, 3, 0) -> [10, 20, 30]
  chunkAt(src, 3, 1) -> [40, 50, 60]

TODO move to `util-coll.mjs`.
*/
export function chunkAt(src, size, ind) {
  a.reqIntPos(size)
  a.reqNat(ind)
  return a.laxArr(src).slice((size * ind), (size * (ind + 1)))
}

export function clearStruct(tar) {
  for (const key of a.structKeys(tar)) delete tar[key]
  return tar
}

/*
Similar to `a.zip` and `Object.fromEntries`, but instead of taking a single
sequence of entries, this takes two separate sequences of keys and values,
whose lengths must match. Skips invalid keys for consistency with `a.zip`,
`a.group` and others.
*/
function zip2(keys, vals) {
  keys = a.arr(keys)
  vals = a.arr(vals)

  const keysLen = keys.length
  const valsLen = vals.length
  if (keysLen !== valsLen) throw Error(`zip mismatch: ${keysLen} keys, ${valsLen} values`)

  const out = a.Emp()
  let ind = -1
  while (++ind < keysLen) {
    const key = keys[ind]
    if (a.isKey(key)) out[key] = vals[ind]
  }
  return out
}

/*
TODO better name. Takes a sequence of structs and returns a no-prototype object
that combines all their properties. Similar to `Object.assign(a.Emp(), ...src)`
but doesn't use rest parameters and performs better.
*/
export function combine(src) {return assignMultiple(a.Emp(), src)}

// TODO better name.
export function assignMultiple(tar, src) {
  a.reqStruct(tar)
  for (src of a.values(src)) a.assign(tar, src)
  return tar
}

/*
Takes a dict and converts all dotted keys into nested dicts. Example:

  {one: 10, 'two.three': 20}
  →
  {one: 10, two: {three: 20}}
*/
export function dotNest(src) {
  src = a.laxDict(src)

  // Easy but very suboptimal. TODO remove or optimize.
  for (const key of a.structKeys(src)) {
    if (!hasDot(key)) continue
    src = e.putIn(src, key.split(`.`), src[key])
    delete src[key]
  }

  return src
}

function hasDot(val) {return a.reqStr(val).includes(`.`)}

/*
TODO: avoid random ids, prefer stable non-random sequential ids that don't
change on page reload.

The following version would be better, but is broken due to Preact caching of
DOM elements:

  export class Seq extends a.Emp {
    num = 0
    pre = c.DOM_AVAILABLE ? `dom_` : `ssr_`
    nextId() {return `id_` + a.reqStr(this.pre) + a.reqInt(++this.num)}
  }
*/
export class Seq extends a.Emp {
  nextId() {return ut.rndId()}
}

// TODO move to `util-coll.mjs`.
export function arrCycle(arr, ind, off) {
  arr = a.laxArr(arr)
  return arr[indexCycle(arr.length, ind, off)]
}

export function indexNext(len, ind) {return indexCycle(len, ind, 1)}
export function indexPrev(len, ind) {return indexCycle(len, ind, -1)}

// TODO move to `util-coll.mjs`.
export function indexCycle(len, ind, off) {
  len = a.reqNat(len)
  ind = a.reqInt(ind)
  off = a.reqInt(off)
  return (ind + len + (off % len)) % len
}

export function boolInt(val) {return a.laxBool(val) ? 1 : 0}

export const obsKey = Symbol.for(`obs`)

export function stackLine(off) {
  return a.lines(new Error().stack)[a.laxInt(off) + 1]
}

export function callerName(off) {
  const line = stackLine(a.laxInt(off) + 1)
  if (!line) return ``

  const match = line.match(/(?:(\S+)@)|(?:at (\S+) )/)
  if (!match) return ``

  return a.laxStr(match?.[1] || match?.[2])
}

export function callerFuncName(off) {
  const name = callerName(a.laxInt(off) + 1)
  return name.slice(name.lastIndexOf(`.`) + 1)
}

export function callerFuncNameTitle(off) {
  const name = callerFuncName(a.laxInt(off) + 1)
  if (ut.isCodePointAsciiLetterUpper(name.codePointAt(0))) return name
  return ``
}

export function isValidStrArr(val) {return a.isArrOf(val, a.isValidStr)}
export function reqValidStrArr(val) {return a.reqArrOf(val, a.isValidStr)}
export function optValidStrArr(val) {return a.optArrOf(val, a.isValidStr)}

export function isStrArr(val) {return a.isArrOf(val, a.isStr)}
export function reqStrArr(val) {return a.reqArrOf(val, a.isStr)}
export function optStrArr(val) {return a.optArrOf(val, a.isStr)}

export function reschedulableBooking(bookings, service) {
  return isBookingsReschedulable(bookings, service) ? a.head(bookings) : undefined
}

export function isBookingsReschedulable(bookings, service) {
  a.reqArr(bookings)
  if (a.len(bookings) !== 1) return false
  return isBookingReschedulable(a.head(bookings), service)
}

export function isBookingReschedulable(booking, service) {
  if (!booking || !service) return false
  return (
    booking.status === c.BOOKING_STATUS_PAYMENT_SUCCEEDED &&
    service.relationType === c.SERVICE_RELATION_TYPE_ONE_TO_ONE
  )
}

export function isTypeNotif(val) {
  return val?.type === c.MSG_TYPE_NOTIF
}

export function isUrlPreferLatest(url) {
  a.reqInst(url, a.Url)
  return a.laxBool(url.query.boolOpt(c.QUERY_KEY_PREFER_LATEST))
}

export function urlWithPreferLatest(url, ok) {
  a.reqInst(url, a.Url)
  url = url.clone()
  url.query.set(c.QUERY_KEY_PREFER_LATEST, a.reqBool(ok) || undefined)
  return url
}

export function reqOneOfs(src, ...clss) {
  for (const cls of clss) if (a.isInst(src, cls)) return src
  throw Error(`expected one of: ${clss}`)
}

// Inverse of `reqServerOnly`.
export function reqClientOnly() {
  if (c.DOM_AVAILABLE || c.PUBLIC_RELEASE) return
  throw Error(`unexpected attempt to run client-only code on the server`)
}

function appendDict(one, two) {
  one = a.reqStruct(one)
  two = a.reqStruct(two)
  return {...one, ...two}
}

export function compactDict(val) {
  val = a.reqArr(val)
  return a.fold(val, {}, appendDict)
}

// Safety and compatibility check.
//
//   https://claude.ai/share/054864f3-8883-4e83-9e08-3066f5f0659b
async function sha256Hash(val) {
  // Convert the input string to an ArrayBuffer
  const encoder = new TextEncoder()
  const data = encoder.encode(val)

  // Generate the hash using the subtle crypto API
  const hashBuffer = await crypto.subtle.digest(`SHA-256`, data)

  // Convert the hash to a hex string
  const hashArray = Array.from(new Uint8Array(hashBuffer))
  const hashHex = hashArray.map(byt => byt.toString(16).padStart(2, `0`)).join(``)

  return hashHex
}

// For usage in `template.mjs`.
export async function fbUserStr(user) {
  if (!user) return ``

  return `
    em: '${await sha256Hash(user.email)}',      // Hashed email
    ln: '${await sha256Hash(user.name)}',       // Hashed last name
    external_id: '${await sha256Hash(user.id)}' // Hashed user ID from your system
  `
}